import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { UserPlus, Pencil, Trash2, RotateCcw, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY = { full_name: '', contact_number: '', id_number: '', active: true };

/** Admin-only Assistants roster — add / edit / delete / activate assistants
 *  that drivers and staff can then claim from their pickers. */
export default function AssistantsRegistryPanel() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, else assistant doc
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/assistants-registry?include_inactive=${includeInactive}`, { headers });
      setRows(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      full_name: a.full_name || '',
      contact_number: a.contact_number || '',
      id_number: a.id_number || '',
      active: a.active !== false,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error('Full name is required');
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/assistants-registry/${editing.id}`, form, { headers });
        toast.success('Assistant updated');
      } else {
        await axios.post(`${API}/assistants-registry`, form, { headers });
        toast.success('Assistant added');
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete assistant "${a.full_name}"? This cannot be undone.`)) return;
    setBusyId(a.id);
    try {
      await axios.delete(`${API}/assistants-registry/${a.id}`, { headers });
      toast.success('Deleted');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusyId(''); }
  };

  const forceRelease = async (a) => {
    if (!window.confirm(`Release ${a.full_name} from ${a.linked_to_user_name}?`)) return;
    setBusyId(a.id);
    try {
      await axios.post(`${API}/assistants-registry/${a.id}/release`, {}, { headers });
      toast.success('Released');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Release failed'); }
    finally { setBusyId(''); }
  };

  return (
    <div className="space-y-3" data-testid="assistants-registry-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Users className="h-4 w-4 text-indigo-600" />
          <span className="font-semibold">Assistants Registry</span>
          <span className="text-xs text-muted-foreground">{rows.length} record{rows.length === 1 ? '' : 's'}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} data-testid="asst-toggle-inactive" />
            Show inactive
          </label>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9 gap-1" onClick={openCreate} data-testid="asst-add-btn">
            <UserPlus className="h-4 w-4" /> Add Assistant
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="asst-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">ID Number</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Linked To</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-muted-foreground italic">
                    No assistants yet. Click "Add Assistant" to create one.
                  </td></tr>
                ) : rows.map((a) => (
                  <tr key={a.id} className="border-t" data-testid={`asst-row-${a.id}`}>
                    <td className="px-3 py-2 font-medium">{a.full_name}</td>
                    <td className="px-3 py-2 text-xs">{a.contact_number || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2 text-xs font-mono">{a.id_number || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">
                      {a.active ? <Badge className="bg-emerald-600">Active</Badge>
                        : <Badge className="bg-slate-400">Inactive</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {a.linked_to_user_id ? (
                        <span className="inline-flex items-center gap-1">
                          <Badge variant="outline" className="text-xs">{a.linked_to_role || 'user'}</Badge>
                          <b>{a.linked_to_user_name}</b>
                        </span>
                      ) : <span className="text-muted-foreground italic">Available</span>}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {a.linked_to_user_id && (
                        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => forceRelease(a)} disabled={busyId === a.id} data-testid={`asst-release-${a.id}`}>
                          <RotateCcw className="h-3 w-3" /> Release
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => openEdit(a)} data-testid={`asst-edit-${a.id}`}>
                        <Pencil className="h-3 w-3" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 gap-1 text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => remove(a)} disabled={busyId === a.id} data-testid={`asst-delete-${a.id}`}>
                        <Trash2 className="h-3 w-3" /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="asst-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit assistant' : 'Add assistant'}</DialogTitle>
            <DialogDescription>
              Assistants are picked by drivers and internal staff from their portal. An assistant can only be linked to one person at a time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="e.g. Zola Helper" data-testid="asst-form-name" />
            </div>
            <div>
              <Label className="text-xs">Contact Number</Label>
              <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} placeholder="+27 82 …" data-testid="asst-form-contact" />
            </div>
            <div>
              <Label className="text-xs">ID Number</Label>
              <Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} placeholder="13-digit SA ID" data-testid="asst-form-id" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="asst-form-active" />
              Active — appears in driver / staff pickers
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="asst-form-submit">
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
